/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import * as stylex from '@stylexjs/stylex';
import { type ForwardedRef, createContext, forwardRef, useLayoutEffect, useRef, useState } from 'react';
import { type ContextValue, TextArea as TextAreaCore, type TextAreaProps as TextAreaCoreProps, useContextProps } from 'react-aria-components';
import { $ } from '../../tokens.stylex';

interface InlineTextAreaProps extends Omit<TextAreaCoreProps, 'style' | 'className'> {
  /** Typography variant that determines line-height and letter-spacing. (Default: 'text') */
  variant?: 'text' | 'heading' | 'paragraph';
  /** Font size. Available sizes depend on the chosen variant. (Default: 16) */
  size?: keyof typeof textSizeVariants | keyof typeof headingSizeVariants | keyof typeof paragraphSizeVariants;
  /** Font weight. (Default: 'regular') */
  weight?: 'semibold' | 'medium' | 'regular';
  /** Text color. (Default: 'primary') */
  color?: keyof typeof colorVariants;
  /** Maximum visible lines when not focused. Content beyond this is truncated with ellipsis. */
  maxLines?: 1 | 2 | 3 | 4 | 5;
  style?: stylex.StyleXStyles;
}

const InlineTextAreaContext = createContext<ContextValue<InlineTextAreaProps, HTMLTextAreaElement>>({});

const InlineTextArea = forwardRef((props: InlineTextAreaProps, ref: ForwardedRef<HTMLTextAreaElement>) => {
  [props, ref] = useContextProps(props, ref, InlineTextAreaContext);

  const {
    style,
    variant = 'text',
    size = 16,
    weight = 'regular',
    color = 'primary',
    maxLines,
    onFocus,
    onBlur,
    onInput,
    value,
    ...textAreaProps
  } = props;

  const measureRef = useRef<HTMLSpanElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  const isClamped = maxLines != null && !isFocused;

  useLayoutEffect(() => {
    const textAreaEl = typeof ref === 'object' && ref?.current;
    if (measureRef.current && textAreaEl) {
      measureRef.current.textContent = textAreaEl.value || '\u00A0';
    }
  });

  // Resolve variant-specific typography styles
  let sizeStyle: stylex.StyleXStyles | undefined;
  let letterSpacingStyle: stylex.StyleXStyles | undefined;
  const letterSpacingKey = `${weight}${size}`;
  if (variant === 'heading') {
    sizeStyle = headingSizeVariants[size as keyof typeof headingSizeVariants];
    letterSpacingStyle = headingLetterSpacingVariants[letterSpacingKey as keyof typeof headingLetterSpacingVariants];
  } else if (variant === 'paragraph') {
    sizeStyle = paragraphSizeVariants[size as keyof typeof paragraphSizeVariants];
    letterSpacingStyle = paragraphLetterSpacingVariants[letterSpacingKey as keyof typeof paragraphLetterSpacingVariants];
  } else {
    sizeStyle = textSizeVariants[size as keyof typeof textSizeVariants];
    letterSpacingStyle = textLetterSpacingVariants[letterSpacingKey as keyof typeof textLetterSpacingVariants];
  }

  return (
    <div
      {...stylex.props(
        styles.base,
        styles.typography,
        sizeStyle,
        colorVariants[color],
        weightVariants[weight],
        letterSpacingStyle,
        style,
      )}>
      <span
        ref={measureRef}
        aria-hidden
        {...stylex.props(
          styles.measure,
          isClamped && styles.measureVisible,
          isClamped && maxLines != null && clampVariants[maxLines],
        )}>
        {value || '\u00A0'}
      </span>
      <TextAreaCore
        ref={ref}
        cols={1}
        rows={1}
        value={value}
        onInput={(e) => {
          if (measureRef.current) {
            measureRef.current.textContent = e.currentTarget.value || '\u00A0';
          }
          onInput?.(e);
        }}
        onFocus={(e) => {
          setIsFocused(true);
          requestAnimationFrame(() => {
            ref.current?.select();
          });

          onFocus?.(e);
        }}
        onBlur={(e) => {
          setIsFocused(false);
          onBlur?.(e);
        }}
        {...textAreaProps}
        {...stylex.props(styles.textarea, isClamped && styles.textareaHidden)}
      />
    </div>
  );
});

const styles = stylex.create({
  base: {
    backgroundColor: {
      default: 'transparent',
      ':hover:not(:focus-within)': $.opacity06,
    },
    cursor: {
      default: 'text',
      ':is([data-disabled])': 'not-allowed',
    },
    display: 'inline-grid',
    transitionDuration: '0.15s',
    transitionProperty: 'background-color',
    transitionTimingFunction: 'ease-in-out',
  },
  textarea: {
    font: 'inherit',
    gridArea: '1 / 1 / auto / auto',
    margin: 0,
    padding: 0,
    borderStyle: 'none',
    outline: 'none',
    overflow: 'hidden',
    backgroundColor: 'transparent',
    boxSizing: 'border-box',
    caretColor: $.textInteractive,
    color: {
      default: 'inherit',
      ':disabled': $.textDisabled,
    },
    letterSpacing: 'inherit',
    resize: 'none',
    minWidth: 0,
    '::selection': { backgroundColor: $.surfaceHighlightMedium },
  },
  textareaHidden: {
    caretColor: 'transparent',
    color: 'transparent',
  },
  measure: {
    gridArea: '1 / 1 / auto / auto',
    padding: 0,
    pointerEvents: 'none',
    visibility: 'hidden',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  measureVisible: {
    visibility: 'visible',
  },
  typography: {
    fontFamily: 'Inter',
    fontFeatureSettings: 'liga off, clig off',
    fontStyle: 'normal',
  },
});

const colorVariants = stylex.create({
  primary: { color: $.textPrimary },
  secondary: { color: $.textSecondary },
  tertiary: { color: $.textTertiary },
  quaternary: { color: $.textQuaternary },
  placeholder: { color: $.textPlaceholder },
  disabled: { color: $.textDisabled },
  inverted: { color: $.textInverted },
  interactive: { color: $.textInteractive },
  interactiveHover: { color: $.textInteractiveHover },
  positive: { color: $.textPositive },
  positiveHover: { color: $.textPositiveHover },
  critical: { color: $.textCritical },
  criticalHover: { color: $.textCriticalHover },
  white: { color: $.textWhite },
  black: { color: $.textBlack },
});

const weightVariants = stylex.create({
  semibold: { fontWeight: 600 },
  medium: { fontWeight: 500 },
  regular: { fontWeight: 400 },
});

const textSizeVariants = stylex.create({
  20: { fontSize: 20, lineHeight: '24px' },
  18: { fontSize: 18, lineHeight: '22px' },
  16: { fontSize: 16, lineHeight: '20px' },
  14: { fontSize: 14, lineHeight: '18px' },
  13: { fontSize: 13, lineHeight: '16px' },
  12: { fontSize: 12, lineHeight: '16px' },
  11: { fontSize: 11, lineHeight: '14px' },
  10: { fontSize: 10, lineHeight: '12px' },
  8: { fontSize: 8, lineHeight: '10px' },
});

const textLetterSpacingVariants = stylex.create({
  semibold20: { letterSpacing: -0.45 },
  medium20: { letterSpacing: -0.5 },
  regular20: { letterSpacing: -0.55 },
  semibold18: { letterSpacing: -0.4 },
  medium18: { letterSpacing: -0.45 },
  regular18: { letterSpacing: -0.5 },
  semibold16: { letterSpacing: -0.4 },
  medium16: { letterSpacing: -0.45 },
  regular16: { letterSpacing: -0.5 },
  semibold14: { letterSpacing: -0.35 },
  medium14: { letterSpacing: -0.4 },
  regular14: { letterSpacing: -0.45 },
  semibold13: { letterSpacing: -0.3 },
  medium13: { letterSpacing: -0.35 },
  regular13: { letterSpacing: -0.4 },
  semibold12: { letterSpacing: -0.3 },
  medium12: { letterSpacing: -0.35 },
  regular12: { letterSpacing: -0.4 },
  semibold11: { letterSpacing: -0.2 },
  medium11: { letterSpacing: -0.25 },
  regular11: { letterSpacing: -0.35 },
  semibold10: { letterSpacing: -0.2 },
  medium10: { letterSpacing: -0.25 },
  regular10: { letterSpacing: -0.3 },
  semibold8: { letterSpacing: 0 },
  medium8: { letterSpacing: 0 },
  regular8: { letterSpacing: 0 },
});

const headingSizeVariants = stylex.create({
  72: { fontSize: 72, lineHeight: '88px' },
  64: { fontSize: 64, lineHeight: '78px' },
  52: { fontSize: 52, lineHeight: '66px' },
  48: { fontSize: 48, lineHeight: '58px' },
  40: { fontSize: 40, lineHeight: '48px' },
  32: { fontSize: 32, lineHeight: '42px' },
  28: { fontSize: 28, lineHeight: '38px' },
  24: { fontSize: 24, lineHeight: '34px' },
  20: { fontSize: 20, lineHeight: '28px' },
  18: { fontSize: 18, lineHeight: '26px' },
  16: { fontSize: 16, lineHeight: '24px' },
});

const headingLetterSpacingVariants = stylex.create({
  semibold72: { letterSpacing: -2.16 },
  medium72: { letterSpacing: -2.52 },
  regular72: { letterSpacing: -2.88 },
  semibold64: { letterSpacing: -1.6 },
  medium64: { letterSpacing: -1.92 },
  regular64: { letterSpacing: -2.24 },
  semibold52: { letterSpacing: -1.04 },
  medium52: { letterSpacing: -1.3 },
  regular52: { letterSpacing: -1.56 },
  semibold48: { letterSpacing: -0.72 },
  medium48: { letterSpacing: -0.96 },
  regular48: { letterSpacing: -1.2 },
  semibold40: { letterSpacing: -0.4 },
  medium40: { letterSpacing: -0.6 },
  regular40: { letterSpacing: -0.8 },
  semibold32: { letterSpacing: -0.16 },
  medium32: { letterSpacing: -0.32 },
  regular32: { letterSpacing: -0.48 },
  semibold28: { letterSpacing: 0 },
  medium28: { letterSpacing: 0 },
  regular28: { letterSpacing: 0 },
  semibold24: { letterSpacing: 0 },
  medium24: { letterSpacing: 0 },
  regular24: { letterSpacing: 0 },
  semibold20: { letterSpacing: 0 },
  medium20: { letterSpacing: 0 },
  regular20: { letterSpacing: 0 },
  semibold18: { letterSpacing: 0 },
  medium18: { letterSpacing: 0 },
  regular18: { letterSpacing: 0 },
  semibold16: { letterSpacing: 0 },
  medium16: { letterSpacing: 0 },
  regular16: { letterSpacing: 0 },
});

const paragraphSizeVariants = stylex.create({
  20: { fontSize: 20, lineHeight: '34px' },
  18: { fontSize: 18, lineHeight: '32px' },
  16: { fontSize: 16, lineHeight: '28px' },
  14: { fontSize: 14, lineHeight: '26px' },
  12: { fontSize: 12, lineHeight: '24px' },
});

const paragraphLetterSpacingVariants = stylex.create({
  semibold20: { letterSpacing: -0.2 },
  medium20: { letterSpacing: -0.2 },
  regular20: { letterSpacing: -0.2 },
  semibold18: { letterSpacing: -0.2 },
  medium18: { letterSpacing: -0.2 },
  regular18: { letterSpacing: -0.2 },
  semibold16: { letterSpacing: -0.15 },
  medium16: { letterSpacing: -0.15 },
  regular16: { letterSpacing: -0.15 },
  semibold14: { letterSpacing: -0.15 },
  medium14: { letterSpacing: -0.15 },
  regular14: { letterSpacing: -0.15 },
  semibold12: { letterSpacing: -0.1 },
  medium12: { letterSpacing: -0.1 },
  regular12: { letterSpacing: -0.1 },
});

const clampVariants = stylex.create({
  1: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  2: {
    overflow: 'hidden',
    WebkitBoxOrient: 'vertical' as never,
    WebkitLineClamp: 2,
    display: '-webkit-box',
  },
  3: {
    overflow: 'hidden',
    WebkitBoxOrient: 'vertical' as never,
    WebkitLineClamp: 3,
    display: '-webkit-box',
  },
  4: {
    overflow: 'hidden',
    WebkitBoxOrient: 'vertical' as never,
    WebkitLineClamp: 4,
    display: '-webkit-box',
  },
  5: {
    overflow: 'hidden',
    WebkitBoxOrient: 'vertical' as never,
    WebkitLineClamp: 5,
    display: '-webkit-box',
  },
});

export { InlineTextArea, InlineTextAreaContext, type InlineTextAreaProps };
